import { HostInput, runProgram } from "idfweb-wasix";
import { exists, writeFile } from "idfweb-vfs";

import { EXECUTION_POLICIES, GUEST_TMP, GUEST_USR, type Toolchain } from "./toolchain.ts";

/**
 * The ESP32-C3 and ESP32-C6 emulator (omars-espemu) as a guest process: it
 * runs a flash image, and its stdin and stdout are the chip's console UART.
 */

export const ESPEMU = `${GUEST_USR}/bin/espemu`;

/** The chips the emulator has a machine for. */
export const EMULATED_TARGETS: readonly string[] = ["esp32c3", "esp32c6"];

/** Where a chip's ROM image is, in `roms/`: the emulator's working directory for it. */
function romDirectory(target: string): string {
    return `${GUEST_USR}/share/espemu/${target}`;
}

export interface Emulation {
    /** Bytes to the console UART. */
    write(bytes: Uint8Array): void;
    /** Stop the emulator. */
    stop(): void;
    /** The exit code, -1 when it was stopped; `failure` when it could not run. */
    readonly exited: Promise<{ exitCode: number; failure?: string }>;
}

export interface EmulationOptions {
    /** Console output, as the chip sends it. */
    onOutput(bytes: Uint8Array): void;
    /** What the emulator itself reports on stderr. */
    onLog?: ((text: string) => void) | undefined;
}

let nextImage = 0;

/**
 * Run `image`, a whole flash from address 0, on an emulated `target` chip in
 * real time until it is stopped. The flash is the smallest size the emulator
 * models that holds the image and `flashBytes`, the size the build is for.
 */
export function startEmulator(
    tc: Toolchain,
    target: string,
    image: Uint8Array,
    flashBytes: number,
    opts: EmulationOptions,
): Emulation {
    const cwd = romDirectory(target);
    if (!EMULATED_TARGETS.includes(target) || !exists(tc.vfs, cwd)) {
        throw new Error(`The emulator has no ${target} in the toolchain for this target.`);
    }
    const flash = erasedFlash(Math.max(image.length, flashBytes));
    flash.set(image);
    const path = `${GUEST_TMP}/espemu-${nextImage++}.bin`;
    writeFile(tc.vfs, path, flash);

    const input = new HostInput();
    const stop = new AbortController();
    const decoder = new TextDecoder();
    const run = runProgram({
        vfs: tc.vfs,
        program: ESPEMU,
        args: [ESPEMU, "--target", target, "--flash", path],
        env: {},
        cwd,
        preopens: tc.preopens,
        policies: EXECUTION_POLICIES,
        trace: tc.trace,
        stdin: input.pipe,
        signal: stop.signal,
        onStdout: opts.onOutput,
        onStderr: (bytes) => opts.onLog?.(decoder.decode(bytes, { stream: true })),
    });
    const exited = run.then(({ exitCode, failure }) => {
        try {
            tc.vfs.unlink(path);
        } catch {
            // Gone already; the run's end is what matters to the caller.
        }
        return failure === undefined ? { exitCode } : { exitCode, failure: failure.message };
    });
    return {
        write: (bytes) => input.write(bytes),
        stop: () => {
            input.close();
            stop.abort();
        },
        exited,
    };
}

/** The flash chips the emulator models, by size. */
const FLASH_SIZES = [2, 4, 8, 16].map((megabytes) => megabytes * 1024 * 1024);

function erasedFlash(atLeast: number): Uint8Array {
    const size = FLASH_SIZES.find((size) => size >= atLeast);
    if (size === undefined) throw new Error(`The emulator has no flash of ${atLeast} bytes or more.`);
    return new Uint8Array(size).fill(0xff);
}
